import { countries } from '@/consts/languageAccordance';
import {
    ThemeVariables,
    variables as defaultVariables,
    colors as defaultColors,
} from '../mpanels/variables';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';

const colors = {
    ...defaultColors,
    white: 'rgba(255, 255, 255, 1)',
    primary: {
        '100': 'rgba(227, 237, 247, 1)',
        '200': 'rgba(199, 219, 239, 1)',
        '300': 'rgba(176, 199, 226, 1)',
        '400': 'rgba(143, 183, 224, 1)',
        '500': 'rgba(115, 165, 216, 1)',
        '600': 'rgba(94, 136, 178, 1)',
        '700': 'rgba(72, 106, 141, 1)',
        '800': 'rgba(51, 77, 103, 1)',
        '900': 'rgba(29, 47, 66, 1)',
    },
    secondary: {
        '100': 'rgba(182, 193, 206, 1)',
        '200': 'rgba(149, 163, 179, 1)',
        '300': 'rgba(117, 134, 153, 1)',
        '400': 'rgba(84, 104, 126, 1)',
        '500': 'rgba(52, 74, 99, 1)',
        '600': 'rgba(42, 59, 79, 1)',
        '700': 'rgba(31, 44, 59, 1)',
        '800': 'rgba(21, 30, 40, 1)',
        '900': 'rgba(10, 15, 20, 1)',
    },
    neutral: {
        '50': 'rgba(249, 249, 250, 1)',
        '100': 'rgba(233, 236, 239, 1)',
        '200': 'rgba(214, 214, 216, 1)',
        '300': 'rgba(176, 180, 192, 1)',
        '400': 'rgba(150, 155, 171, 1)',
        '500': 'rgba(124, 130, 150, 1)',
        '600': 'rgba(98, 101, 111, 1)',
        '700': 'rgba(74, 78, 90, 1)',
        '800': 'rgba(50, 52, 60, 1)',
        '900': 'rgba(25, 26, 30, 1)',
    },
    success: 'rgba(111, 209, 132, 1)',
    info: 'rgba(115, 165, 216, 1)',
    warning: 'rgba(255, 163, 87, 1)',
    error: 'rgba(242, 84, 91, 1)',
};

const palette = {
    ...defaultVariables.colors.palette,
    primary: {
        ...colors.primary,
        main: colors.primary['500'],
        contrastText: colors.white,
    },
    secondary: {
        ...colors.secondary,
        main: colors.secondary['500'],
    },
    tertiary: {
        main: colors.success,
    },
    border: {
        main: colors.neutral['100'],
    },
    background: {
        default: colors.neutral['50'],
        menuItemHover: colors.neutral['100'],
    },
    success: {
        main: colors.success,
    },
    info: {
        main: colors.info,
    },
    warning: {
        main: colors.warning,
    },
    error: {
        main: colors.error,
    },
    text: {
        primary: colors.secondary['600'],
    },
};

const fonts = {
    primary: 'Open Sans, sans-serif',
    secondary: 'Open Sans, sans-serif',
    additional: 'Open Sans, sans-serif',
    tertiary: 'Poppins, serif',
    body: {
        fontSize: '1rem',
    },
};

type MedPlanetThemeVariables = ThemeVariables & {
    colors: {
        palette: ThemeVariables['colors']['palette'] & typeof palette;
    };
};

export const variables: MedPlanetThemeVariables = {
    themeName: 'medplanet',
    sortOrder: {
        ...defaultVariables.sortOrder,
        locale: ['fr-FR'],
        countriesSelectOptions: [[String(countries['fr'].phraseId)], ['*']],
    },
    colors: {
        palette,
        links: {
            main: colors.primary['500'],
            hover: colors.primary['300'],
            active: colors.primary['600'],
            disabled: colors.primary['500'],
        },
        error: {
            main: colors.error,
        },
        other: {
            badge: {
                topMenu: {
                    color: colors.white,
                    background: colors.warning,
                },
            },
            chip: {
                default: {
                    background: addOpacityToThemeColor(colors.primary['500'], 0.1),
                    text: colors.primary['500'],
                },
                methodologyType: {
                    telephone: {
                        background: 'rgba(12, 147, 156, 0.1)',
                        text: 'rgba(12, 147, 156)',
                    },
                    online: {
                        background: 'rgba(165, 15, 218, 0.1)',
                        text: 'rgba(165, 15, 218)',
                    },
                    inPerson: {
                        background: 'rgba(115, 165, 216, 0.1)',
                        text: 'rgba(115, 165, 216)',
                    },
                },
            },
            table: {
                evenRowHighlight: addOpacityToThemeColor(colors.primary['300'], 0.1),
                hoverHighlight: addOpacityToThemeColor(colors.primary['500'], 0.1),
                headerText: palette.text.primary,
                text: palette.text.primary,
                hoverText: colors.primary['500'],
            },
            background: {
                ...defaultVariables.colors.other.background,
                header: colors.neutral['50'],
                topMenu: colors.neutral['500'],
                footer: colors.secondary['500'],
                highlight: addOpacityToThemeColor(palette.primary.main, 0.1),
                highlightHover: addOpacityToThemeColor(palette.primary.main, 0.15),
                success: addOpacityToThemeColor(colors.success, 0.1),
                warning: addOpacityToThemeColor(colors.warning, 0.1),
                info: addOpacityToThemeColor(colors.info, 0.1),
                error: addOpacityToThemeColor(colors.error, 0.1),
                divideline: 'rgba(124, 130, 150, 0.1)',
                preloader: palette.primary.main,
            },
            text: {
                ...defaultVariables.colors.other.text,
                header: palette.text.primary,
                registrationDescription: colors.white,
                footer: colors.white,
                copyrightText: palette.text.primary,
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
            },
            inputs: {
                default: colors.neutral['200'],
                hover: colors.primary['500'],
                focus: colors.secondary['500'],
            },
            icon: {
                stroke: {
                    primary: colors.primary['500'],
                    secondary: colors.primary['500'],
                    tertiary: colors.secondary['500'],
                },
                background: {
                    primary: addOpacityToThemeColor(colors.primary['500'], 0.2),
                    secondary: addOpacityToThemeColor(colors.primary['500'], 0.2),
                    tertiary: addOpacityToThemeColor(colors.secondary['500'], 0.2),
                },
            },
            notifications: {
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
            },
        },
    },
    fonts,
    borderRadius: {
        primary: '24px',
        secondary: '8px',
        tertiary: '6px',
        topMenu: 'initial',
        mobileTopMenu: '0 0 10px 10px',
    },
    padding: {
        ...defaultVariables.padding,
        inputsDefault: '8px 16px',
        tableCellPadding: '12px 16px',
        elementPadding: '0 0 12px 0',
    },
    margin: {
        ...defaultVariables.margin,
    },
    boxShadows: {
        popoverShadow: 'none',
        tableShadow: 'none',
        tableSelectedShadow: `inset 0 2px 4px 2px ${addOpacityToThemeColor(
            palette.primary['500'],
            0.3,
        )}`,
        headerShadow: '0px 1px 0px 0px rgba(206, 212, 218)',
        buttonShadow: 'initial',
        inputShadow: 'initial',
        tooltipShadow: `0px 4px 4px 0px ${addOpacityToThemeColor(palette.text.primary, 0.16)}`,
        descriptiveShadow: 'initial',
    },
    layout: {
        helpCentreInsteadFaq: true,
        header: {
            logos: {
                4: '/images/themes/medplanet/subpanel/logo-header-patients.svg',
            },
        },
        home: {
            hasBackgroundImage: 'fullwidth',
            pageTitle: {
                textAlign: 'left',
                position: 'top',
            },
            description: {
                visible: true,
                position: 'top',
                hasMobileTitle: true,
            },
            picBlock: {
                visible: true,
                position: 'bottom',
            },
            iconsBlock: {
                visible: true,
                position: 'bottom',
                inlineIcons: true,
                paperBox: true,
            },
            registrationForm: {
                fullwidthForm: false,
                showStepTitle: false,
                stepperOrientation: 'horizontal',
            },
        },
        footer: {
            fullWidth: false,
            logoPosition: 'right',
            themeColorSocialIcons: true,
            companyLinkVisible: false,
            inlineFollowUs: true,
            hasFollowUsTitle: false,
        },
    },
    cookie: {
        button: {
            hover: {
                color: 'white',
                backgroundColor: 'rgba(176, 199, 226, 1)',
            },
            borderRadius: '24px',
            shadow: 'initial',
        },
        toggle: {
            initial: {
                selected: {
                    toggleColor: colors.secondary['500'],
                    backgroundColor: colors.secondary['100'],
                },
                unselected: {
                    toggleColor: 'rgba(250, 250, 250, 1)',
                    backgroundColor: 'rgba(173, 181, 189, 0.38)',
                },
            },
            disabled: {
                toggleColor: 'rgba(173, 181, 189, 1)',
                backgroundColor: 'rgba(173, 181, 189, 0.38)',
            },
        },
    },
    typography: {
        fontFamily: fonts.primary,
        h1: {
            color: palette.secondary.main,
            fontFamily: fonts.tertiary,
            fontSize: '2rem', // 32px
            fontWeight: '700',
        },
        h2: {
            color: palette.secondary.main,
            fontFamily: fonts.tertiary,
            fontSize: '1.75rem', // 28px
            fontWeight: '700',
        },
        h3: {
            color: palette.secondary.main,
            fontFamily: fonts.tertiary,
            fontSize: '1.5rem', // 24px
            fontWeight: '700',
        },
        h4: {
            color: palette.secondary.main,
            fontSize: '1.125rem', // 18px
            fontFamily: fonts.tertiary,
            fontWeight: '700',
        },
        h5: {
            color: palette.secondary.main,
            fontSize: '1rem', // 16px
            fontFamily: fonts.tertiary,
            fontWeight: '700',
        },
        h6: {
            color: palette.secondary.main,
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.tertiary,
            fontWeight: '700',
        },
        body1: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.primary,
            fontWeight: '400',
        },
        body2: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.primary,
            fontWeight: '400',
        },
        subtitle1: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.primary,
            fontWeight: '600',
        },
        subtitle2: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.primary,
            fontWeight: '600',
        },
        subtitle3: {
            fontSize: '0.75rem', // 12px
            fontFamily: fonts.primary,
            fontWeight: '600',
        },
        greyLabel: {
            fontSize: '0.75rem', // 12px
            fontFamily: fonts.primary,
        },
        link: {
            textDecoration: 'none',
            '&:active': {
                textDecoration: 'inherit',
            },
            '&:hover': {
                textDecoration: 'inherit',
            },
            '&:disabled': {
                textDecoration: 'inherit',
            },
        },
    },
};
